// ─────────────────────────────────────────────────────────────────────────────
// Flora — the garden's plant glyphs, styled to the language you're learning.
// Each language grows a culturally-evocative palette of six species (Andalusian
// for Spanish, Brazilian for Portuguese, Nordic for Finnish). Forms are pure
// deterministic SVG (anchored at the origin, growing upward) so a garden looks
// the same on every visit. Foliage paints in var(--success), blossoms in
// var(--accent); a few signature hues are fixed so olives, oranges, lavender,
// birch-bark etc. read true in both light and dark themes.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react'
import type { Language } from './Scenes.js'

type Sig = { sway: number }
type Renderer = (s: Sig) => React.ReactNode

const G = 'var(--success)'      // foliage
const A = 'var(--accent)'       // blossom / signature
const BARK = 'var(--border2)'
const BG = 'var(--bg)'

// ── Spanish · Andalucía: sunflower, olive, orange, lavender, agave, vine ──────
const ES: Renderer[] = [
  ({ sway }) => ( // girasol — sunflower
    <g>
      <path d={`M0 0 Q${sway} -18 ${sway} -30`} stroke={G} strokeWidth="2.2" fill="none" strokeLinecap="round" />
      <path d={`M${sway} -14 Q-8 -16 -3 -10`} stroke={G} strokeWidth="1.8" fill={G} fillOpacity="0.3" strokeLinecap="round" />
      {Array.from({ length: 12 }).map((_, i) => <ellipse key={i} cx={sway} cy={-37} rx="2.6" ry="7" fill={A} opacity="0.9" transform={`rotate(${i * 30} ${sway} -30)`} />)}
      <circle cx={sway} cy={-30} r="4.5" fill="#6B4A1E" />
    </g>
  ),
  ({ sway }) => ( // olivo — olive tree
    <g>
      <path d="M-1 0 L0 -16 M0 -8 L5 -13" stroke={BARK} strokeWidth="2.6" strokeLinecap="round" />
      <ellipse cx={sway} cy={-24} rx="13" ry="9" fill={G} opacity="0.5" />
      <ellipse cx={sway - 5} cy={-22} rx="7" ry="6" fill={G} opacity="0.65" />
      <circle cx={sway + 4} cy={-25} r="1.6" fill="#5B6B2E" />
      <circle cx={sway - 6} cy={-21} r="1.6" fill="#5B6B2E" />
    </g>
  ),
  ({ sway }) => ( // naranjo — orange tree
    <g>
      <path d="M0 0 L0 -14" stroke={BARK} strokeWidth="2.6" strokeLinecap="round" />
      <circle cx={sway} cy={-24} r="12" fill={G} opacity="0.7" />
      <circle cx={sway - 5} cy={-20} r="2" fill="#E8852E" />
      <circle cx={sway + 5} cy={-27} r="2" fill="#E8852E" />
      <circle cx={sway + 1} cy={-22} r="2" fill="#E8852E" />
    </g>
  ),
  ({ sway }) => ( // lavanda — lavender
    <g>
      {[-6, 0, 6].map((dx, i) => (
        <g key={i}>
          <path d={`M${dx} 0 Q${dx + sway} -10 ${dx + sway} -20`} stroke={G} strokeWidth="1.8" fill="none" strokeLinecap="round" />
          {[0, 1, 2, 3].map(k => <circle key={k} cx={dx + sway} cy={-20 - k * 4} r="2" fill="#9B7BD0" opacity="0.9" />)}
        </g>
      ))}
    </g>
  ),
  ({ sway }) => ( // agave
    <g>
      {[-50, -25, 0, 25, 50, 90].map((deg, i) => <path key={i} d="M0 0 L0 -26" stroke="#6E9A78" strokeWidth="3" strokeLinecap="round" transform={`rotate(${deg + sway})`} opacity="0.85" />)}
    </g>
  ),
  ({ sway }) => ( // vid — grapevine
    <g>
      <path d={`M0 0 Q${sway - 6} -10 ${sway} -18 Q${sway + 6} -24 ${sway} -28`} stroke={G} strokeWidth="2" fill="none" strokeLinecap="round" />
      {[[0, -22], [-4, -18], [4, -18], [-2, -14], [2, -14], [0, -10]].map(([dx, dy], i) => <circle key={i} cx={sway + dx} cy={dy} r="2.4" fill="#7B4B8A" opacity="0.9" />)}
    </g>
  ),
]

// ── Portuguese · Brasil: palm, ipê, coffee, hibiscus, banana, bromeliad ──────
const PT: Renderer[] = [
  ({ sway }) => ( // palmeira — palm
    <g>
      <path d={`M0 0 Q${sway * 0.5} -14 ${sway} -26`} stroke={BARK} strokeWidth="3" fill="none" strokeLinecap="round" />
      {[-60, -30, 0, 30, 60].map((deg, i) => <path key={i} d="M0 0 Q12 -2 22 4" stroke={G} strokeWidth="2.4" fill="none" strokeLinecap="round" transform={`translate(${sway} -26) rotate(${deg + 180})`} />)}
    </g>
  ),
  ({ sway }) => ( // ipê amarelo
    <g>
      <path d="M0 0 L0 -18 M0 -10 L6 -15" stroke={BARK} strokeWidth="2.6" strokeLinecap="round" />
      <circle cx={sway} cy={-28} r="11" fill="#E8B43A" opacity="0.85" />
      <circle cx={sway - 7} cy={-24} r="6" fill="#E8B43A" opacity="0.7" />
      <circle cx={sway + 7} cy={-25} r="6" fill="#E8B43A" opacity="0.7" />
    </g>
  ),
  ({ sway }) => ( // café — coffee shrub
    <g>
      <path d="M-1 0 L0 -12" stroke={BARK} strokeWidth="2.2" strokeLinecap="round" />
      <ellipse cx={sway} cy={-18} rx="10" ry="9" fill={G} opacity="0.65" />
      <circle cx={sway - 5} cy={-16} r="1.8" fill="#C0392B" />
      <circle cx={sway + 5} cy={-19} r="1.8" fill="#C0392B" />
      <circle cx={sway} cy={-14} r="1.8" fill="#C0392B" />
    </g>
  ),
  ({ sway }) => ( // hibisco — hibiscus
    <g>
      <path d={`M0 0 Q${sway} -12 ${sway} -22`} stroke={G} strokeWidth="2" fill="none" strokeLinecap="round" />
      {[0, 72, 144, 216, 288].map(d => <ellipse key={d} cx={sway} cy={-30} rx="4" ry="8" fill="#E0567B" opacity="0.85" transform={`rotate(${d} ${sway} -24)`} />)}
      <circle cx={sway} cy={-24} r="2.4" fill="#E8B43A" />
    </g>
  ),
  ({ sway }) => ( // bananeira — banana
    <g>
      <path d="M0 0 L0 -10" stroke={BARK} strokeWidth="3" strokeLinecap="round" />
      {[-40, 0, 40].map((deg, i) => <ellipse key={i} cx="0" cy="-22" rx="6" ry="15" fill={G} opacity="0.6" transform={`translate(${sway} 0) rotate(${deg})`} />)}
    </g>
  ),
  ({ sway }) => ( // bromélia — bromeliad
    <g>
      {[-55, -28, 0, 28, 55].map((deg, i) => <path key={i} d="M0 0 L0 -22" stroke={G} strokeWidth="3.4" strokeLinecap="round" transform={`rotate(${deg + sway})`} opacity="0.8" />)}
      <circle cx={sway} cy={-6} r="3" fill="#D24B4B" />
    </g>
  ),
]

// ── Finnish · Suomi: birch, pine, lingonberry, fern, chanterelle, cotton-grass ─
const FI: Renderer[] = [
  ({ sway }) => ( // koivu — birch
    <g>
      <path d="M0 0 L0 -28" stroke="#D8D2C0" strokeWidth="3" strokeLinecap="round" />
      <path d="M-1 -8 h3 M-1 -16 h3 M-1 -22 h3" stroke="#2C2A24" strokeWidth="1.4" />
      <circle cx={sway} cy={-31} r="8" fill={G} opacity="0.5" />
      <circle cx={sway - 5} cy={-27} r="5" fill={G} opacity="0.45" />
      <circle cx={sway + 5} cy={-28} r="5" fill={G} opacity="0.45" />
    </g>
  ),
  ({ sway }) => ( // kuusi — spruce
    <g>
      <path d="M-0.5 0 L0 -8" stroke={BARK} strokeWidth="2.4" strokeLinecap="round" />
      <path d={`M${sway} -34 L-9 -8 L9 -8 Z`} fill="#2F6E4A" opacity="0.85" />
      <path d={`M${sway} -24 L-7 -2 L7 -2 Z`} fill="#357A52" opacity="0.85" />
    </g>
  ),
  ({ sway }) => ( // puolukka — lingonberry
    <g>
      <ellipse cx={sway} cy={-7} rx="11" ry="7" fill={G} opacity="0.6" />
      <circle cx={sway - 5} cy={-8} r="1.8" fill="#C0392B" />
      <circle cx={sway + 4} cy={-9} r="1.8" fill="#C0392B" />
      <circle cx={sway} cy={-6} r="1.8" fill="#C0392B" />
    </g>
  ),
  ({ sway }) => ( // saniainen — fern
    <g>
      {[-30, -10, 10, 30].map((deg, i) => <path key={i} d="M0 0 Q4 -16 0 -30" stroke={G} strokeWidth="2.2" fill="none" strokeLinecap="round" transform={`rotate(${deg + sway})`} />)}
    </g>
  ),
  ({ sway }) => ( // kantarelli — chanterelle
    <g>
      <path d={`M-2 0 Q-3 -10 ${sway} -12 Q3 -10 2 0 Z`} fill="#E0A12E" />
      <path d={`M${sway - 9} -12 Q${sway} -20 ${sway + 9} -12 Q${sway} -8 ${sway - 9} -12 Z`} fill="#EFB347" />
    </g>
  ),
  ({ sway }) => ( // tupasvilla — cotton-grass
    <g>
      {[-6, 0, 6].map((dx, i) => (
        <g key={i}>
          <path d={`M${dx} 0 L${dx + sway} -22`} stroke="#8FA37E" strokeWidth="1.6" strokeLinecap="round" />
          <circle cx={dx + sway} cy={-24} r="3.2" fill="#EDEAE0" opacity="0.92" />
        </g>
      ))}
    </g>
  ),
]

const FLORA: Record<Language, Renderer[]> = { Spanish: ES, Portuguese: PT, Finnish: FI }

export function Plant({ x, groundY, seed, scale, opacity, language = 'Spanish' }: {
  x: number; groundY: number; seed: number; scale: number; opacity: number; language?: Language
}) {
  const set = FLORA[language] ?? ES
  const form = seed % set.length
  const sway = ((seed >> 3) % 5) - 2
  return (
    <g transform={`translate(${x} ${groundY}) scale(${scale})`} opacity={opacity} style={{ transition: 'opacity 0.6s' }}>
      {set[form]({ sway })}
    </g>
  )
}
